import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type TextareaHTMLAttributes,
} from 'react'
import * as RDialog from '@radix-ui/react-dialog'
import { avatarHue, formatTokens, initials } from '../lib/format'
import type { Tag, UserRef } from '../lib/types'

export function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ')
}

// ---------------------------------------------------------------- button

type ButtonVariant = 'primary' | 'default' | 'ghost' | 'danger'

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-md font-medium ' +
  'transition-[background-color,border-color,color,opacity] duration-[140ms] ' +
  'disabled:opacity-45 disabled:pointer-events-none select-none whitespace-nowrap'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] ' +
    'dark:text-[var(--color-void)] dark:font-semibold',
  default:
    'bg-[var(--surface)] text-[var(--ink)] border border-[var(--line)] ' +
    'hover:border-[var(--line-strong)] hover:bg-[var(--sunken)]',
  ghost: 'text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--sunken)]',
  danger:
    'bg-transparent text-[var(--danger)] border border-[var(--line)] ' +
    'hover:border-[var(--danger)] hover:bg-[color-mix(in_srgb,var(--danger)_10%,transparent)]',
}

const BUTTON_SIZES = {
  sm: 'h-7 px-2.5 text-[13px]',
  md: 'h-8 px-3 text-[13px]',
  lg: 'h-10 px-4 text-sm',
  icon: 'h-8 w-8',
  iconSm: 'h-7 w-7',
}

export function Button({
  variant = 'default',
  size = 'md',
  className,
  loading,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: keyof typeof BUTTON_SIZES
  loading?: boolean
}) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
    >
      {loading ? <Spinner /> : children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx(
        'inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent opacity-70',
        className,
      )}
    />
  )
}

// ----------------------------------------------------------------- inputs

const FIELD_BASE =
  'w-full rounded-md bg-[var(--surface)] border border-[var(--line)] px-2.5 py-1.5 ' +
  'text-[13px] placeholder:text-[var(--ink-3)] transition-colors duration-[140ms] ' +
  'hover:border-[var(--line-strong)] focus:border-[var(--accent)] focus:outline-none ' +
  'focus:ring-2 focus:ring-[color-mix(in_srgb,var(--accent)_25%,transparent)]'

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(FIELD_BASE, className)} />
}

export function Textarea({
  className,
  ref,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  // React 19 passes ref as an ordinary prop, so no forwardRef wrapper is
  // needed — it just has to be declared to satisfy the type.
  ref?: React.Ref<HTMLTextAreaElement>
}) {
  return (
    <textarea {...props} ref={ref} className={cx(FIELD_BASE, 'resize-y leading-relaxed', className)} />
  )
}

export function Select({
  className,
  children,
  ...props
}: InputHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return (
    <select {...props} className={cx(FIELD_BASE, 'cursor-pointer', className)}>
      {children}
    </select>
  )
}

/**
 * A labelled form row. `error` is the server's message for this exact field,
 * so a rejected form marks the input that caused it instead of showing a
 * banner and making people guess.
 */
export function Field({
  label, error, hint, children, htmlFor,
}: {
  label: string
  error?: string
  hint?: string
  children: ReactNode
  htmlFor?: string
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="block text-[12px] font-medium text-[var(--ink-2)]"
      >
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-[12px] text-[var(--danger)]">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-[var(--ink-3)]">{hint}</p>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------- avatars

export function Avatar({
  user, size = 22, className,
}: {
  user: UserRef | null
  size?: number
  className?: string
}) {
  if (!user) {
    return (
      <span
        aria-hidden
        style={{ width: size, height: size }}
        className={cx(
          'inline-flex shrink-0 items-center justify-center rounded-full',
          'border border-dashed border-[var(--line-strong)] text-[var(--ink-3)]',
          className,
        )}
      />
    )
  }

  const hue = avatarHue(user.id)
  return (
    <span
      title={user.name}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, Math.round(size * 0.4)),
        background: `oklch(0.62 0.09 ${hue})`,
      }}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        'font-semibold text-white/95 leading-none',
        className,
      )}
    >
      {initials(user.name)}
    </span>
  )
}

export function AvatarStack({ users, max = 3 }: { users: UserRef[]; max?: number }) {
  const shown = users.slice(0, max)
  const extra = users.length - shown.length
  return (
    <span className="flex items-center">
      {shown.map((u, i) => (
        <Avatar
          key={u.id}
          user={u}
          size={20}
          className={cx('ring-2 ring-[var(--surface)]', i > 0 && '-ml-1.5')}
        />
      ))}
      {extra > 0 && (
        <span className="-ml-1.5 inline-flex h-5 items-center rounded-full bg-[var(--sunken)] px-1.5 text-[10px] font-medium text-[var(--ink-2)] ring-2 ring-[var(--surface)]">
          +{extra}
        </span>
      )}
    </span>
  )
}

// ------------------------------------------------------------------ tags

/**
 * A tag chip. The dot carries the tag's colour rather than the whole chip
 * being tinted — a card can hold four tags without turning into a paint chart.
 */
export function TagChip({
  tag, onRemove, className,
}: {
  tag: Tag
  onRemove?: () => void
  className?: string
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded border border-[var(--line)]',
        'bg-[var(--sunken)] px-1.5 py-px text-[11.5px] text-[var(--ink-2)] leading-[1.45]',
        className,
      )}
    >
      <span
        aria-hidden
        className="h-[7px] w-[7px] shrink-0 rounded-full"
        style={{ background: tag.color }}
      />
      {tag.name}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove tag ${tag.name}`}
          className="ml-0.5 -mr-0.5 text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          ×
        </button>
      )}
    </span>
  )
}

// ----------------------------------------------------------------- dialog

export function Dialog({
  open, onOpenChange, title, description, children, width = 480,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: ReactNode
  width?: number
}) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fade-in fixed inset-0 z-40 bg-black/35 backdrop-blur-[2px]" />
        <RDialog.Content
          style={{ maxWidth: width }}
          className={cx(
            'fade-in fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5 shadow-2xl shadow-black/10',
            'max-h-[85vh] overflow-y-auto dark:shadow-black/50',
          )}
        >
          <RDialog.Title className="text-[15px] font-semibold">{title}</RDialog.Title>
          {description && (
            <RDialog.Description className="mt-1 text-[13px] text-[var(--ink-2)]">
              {description}
            </RDialog.Description>
          )}
          <div className="mt-4">{children}</div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}

// ------------------------------------------------------------------ toast

type Toast = { id: number; message: string; tone: 'info' | 'error'; detail?: string }

const ToastContext = createContext<{
  show: (message: string, opts?: { tone?: 'info' | 'error'; detail?: string }) => void
  /** Convenience for the export buttons: reports what landed on the clipboard. */
  showCopied: (label: string, tokens: number) => void
}>({ show: () => {}, showCopied: () => {} })

export const useToast = () => useContext(ToastContext)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)

  const show = useCallback(
    (message: string, opts?: { tone?: 'info' | 'error'; detail?: string }) => {
      const id = nextId.current++
      setToasts((prev) => [...prev, { id, message, tone: opts?.tone ?? 'info', detail: opts?.detail }])
      // Errors stay long enough to read twice; confirmations get out of the way.
      const life = opts?.tone === 'error' ? 6000 : 3200
      window.setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), life)
    },
    [],
  )

  const showCopied = useCallback(
    (label: string, tokens: number) => show(`${label} copied`, { detail: formatTokens(tokens) }),
    [show],
  )

  const value = useMemo(() => ({ show, showCopied }), [show, showCopied])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 left-1/2 z-[100] flex -translate-x-1/2 flex-col items-center gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cx(
              'toast-in pointer-events-auto flex items-center gap-2 rounded-lg border px-3 py-2',
              'text-[13px] shadow-lg shadow-black/10 dark:shadow-black/40',
              t.tone === 'error'
                ? 'border-[var(--danger)] bg-[var(--surface)] text-[var(--danger)]'
                : 'border-[var(--line-strong)] bg-[var(--surface)] text-[var(--ink)]',
            )}
          >
            <span>{t.message}</span>
            {t.detail && (
              <span className="task-key text-[11px] text-[var(--ink-3)]">{t.detail}</span>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

// ------------------------------------------------------------ empty state

/**
 * An empty screen is an instruction, not a shrug: it names what goes here and
 * offers the action that fills it.
 */
/**
 * A titled block on a settings page. Both settings screens are a stack of
 * these, so the rules between them line up whichever page you are on.
 */
export function Section({
  title, hint, children,
}: {
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <section className="mt-7 border-t border-[var(--line)] pt-6">
      <h2 className="text-[14px] font-semibold">{title}</h2>
      {hint && <p className="mt-0.5 text-[12.5px] text-[var(--ink-2)]">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  )
}

export function EmptyState({
  title, hint, action,
}: {
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <p className="text-[14px] font-medium text-[var(--ink-2)]">{title}</p>
      {hint && <p className="max-w-xs text-[13px] text-[var(--ink-3)]">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

// ------------------------------------------------------------------ theme

export type ThemeMode = 'light' | 'dark' | 'system'

const THEME_KEY = 'artsol-theme'

function readMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved
  } catch {
    // Private browsing blocks storage; fall back to following the OS.
  }
  return 'system'
}

const ThemeContext = createContext<{
  mode: ThemeMode
  dark: boolean
  setMode: (mode: ThemeMode) => void
  toggle: () => void
}>({ mode: 'system', dark: false, setMode: () => {}, toggle: () => {} })

export const useTheme = () => useContext(ThemeContext)

/**
 * Theme state, shared so the sidebar toggle and the Settings control cannot
 * disagree about which one is active.
 *
 * "System" is the default and stays live: change the OS appearance and the app
 * follows without a reload. The inline script in index.html applies the same
 * rule before first paint, so a dark-mode user never sees a white flash.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readMode)
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const next = mode === 'dark' || (mode === 'system' && media.matches)
      document.documentElement.classList.toggle('dark', next)
      setDark(next)
    }
    apply()
    try {
      localStorage.setItem(THEME_KEY, mode)
    } catch {
      // Nothing to do; the choice still applies for this session.
    }
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [mode])

  const value = useMemo(
    () => ({
      mode,
      dark,
      setMode: setModeState,
      // The one-click toggle picks the opposite of what is on screen and
      // pins it, which is what somebody clicking a sun icon means.
      toggle: () => setModeState(dark ? 'light' : 'dark'),
    }),
    [mode, dark],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
